import { createBatchEventProcessor, createInstance, createLogger, createPollingProjectConfigManager, ERROR } from '@optimizely/optimizely-sdk';

// Cache for the Optimizely instance and user context
const optimizelyCache = {
  instance: null,
  userContext: null,
};

export const useOptimizely = () => useState('optimizely', () => ({}));

export default defineNuxtPlugin(async (nuxtApp) => {
  const optimizely = useOptimizely();
  const {
    locale,
    locales,
  } = nuxtApp.$i18n;

  try {
    if (false) {
      const appConfig = useRuntimeConfig();
      const currencyStore = useCurrencyStore();
      const sessionStore = useSessionStore();
      const userLang = locales.value.find((l) => l.code === locale.value);

      // Create or get user session cookie
      const optimizelyUserCookie = useCookie('optimizely-aid', {
        domain: appConfig.public.COOKIE_DOMAIN,
        maxAge: 63072000, // 2 years
        path: '/',
        secure: appConfig.public.COOKIE_SECURE,
        sameSite: 'lax',
      });

      // Generate or retrieve user ID
      if (!optimizelyUserCookie.value) {
        optimizelyUserCookie.value = crypto.randomUUID();
      }

      // Initialize Optimizely instance if not exists
      if (!optimizelyCache.instance) {
        const optimizelyKey = appConfig.public.OPTIMIZELY_SDK_KEY;
        const datafileUrl = appConfig.public.OPTIMIZELY_DATAFILE;

        if (!optimizelyKey) {
          console.error('### Optimizely SDK key is not configured');
          return;
        }

        if (!datafileUrl) {
          console.error('### Optimizely datafile URL is not configured');
          return;
        }

        optimizelyCache.instance = createInstance({
          projectConfigManager: createPollingProjectConfigManager({
            sdkKey: optimizelyKey,
            autoUpdate: true,
            updateInterval: 300000, // 5 minutes
            urlTemplate: `${appConfig.public.OPTIMIZELY_DATAFILE}`,
          }),
          eventProcessor: createBatchEventProcessor({
            batchSize: 10,
            flushInterval: 1000,
          }),
          logger: createLogger({ level: ERROR }),
        });

        await optimizelyCache.instance.onReady();
      }

      // Create user context with attributes
      const userAttributes = {
        device: sessionStore.getUserDevice,
        userAgent: sessionStore.getUserUA,
        currency: currencyStore.getCurrentCurrency?.code,
        language: userLang?.tag,
        sessionId: optimizelyUserCookie.value,
      };

      // Create or update user context
      try {
        optimizelyCache.userContext = optimizelyCache.instance.createUserContext(
          optimizelyUserCookie.value,
          userAttributes,
        );
      } catch (error) {
        console.error('### Failed to create Optimizely user context:', error);
        return;
      }

      // Get all feature decisions
      const userDecisions = optimizelyCache.userContext.decideAll();
      const currentEnabledFeatures = Object.keys(userDecisions).filter((ud) => userDecisions[ud].enabled);

      // Process custom feature toggles from cookie
      if (typeof optimizelyUserCookie.value === 'string' && optimizelyUserCookie.value.includes('|')) {
        optimizelyUserCookie.value.split(',').forEach((feature) => {
          if (!feature.trim()) {
            return;
          }

          const [key, status] = feature.split('|');
          if (!key || !status) {
            return;
          }

          const statusLower = status.trim().toLowerCase();
          if (statusLower !== 'on' && statusLower !== 'off') {
            return;
          }

          const featureIndex = currentEnabledFeatures.indexOf(key);

          if (statusLower === 'on' && featureIndex < 0) {
            currentEnabledFeatures.push(key);
          }
          if (statusLower === 'off' && featureIndex >= 0) {
            currentEnabledFeatures.splice(featureIndex, 1);
          }
        });
      }

      // Map feature variables
      optimizely.value = currentEnabledFeatures.reduce((prevValue, feature) => {
        prevValue[feature] = userDecisions[feature]?.variables;
        return prevValue;
      }, {});

      // Provide Optimizely instance to Nuxt app
      nuxtApp.provide('optimizely', {
        instance: optimizelyCache.instance,
        userContext: optimizelyCache.userContext,
      });
    } else {
      // Client-side: Reuse server-provided instance
      const serverOptimizely = nuxtApp.$optimizely;
      if (serverOptimizely) {
        optimizelyCache.instance = serverOptimizely.instance;
        optimizelyCache.userContext = serverOptimizely.userContext;
      }
    }

    // Update Pinia store
    const testingStore = useTestingStore(nuxtApp.$pinia);
    testingStore.setEnabledFeatures(optimizely.value);
  } catch (error) {
    console.error('### Optimizely initialization error:', error);
    optimizely.value = {};
  }

  // Cleanup on plugin unmount
  nuxtApp.hook('close', () => {
    if (optimizelyCache.instance) {
      try {
        optimizelyCache.instance.close();
      } catch (error) {
        console.error('### Error closing Optimizely instance:', error);
      } finally {
        optimizelyCache.instance = null;
        optimizelyCache.userContext = null;
      }
    }
  });
});
